import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "wouter";
import { 
  Users, 
  Building2, 
  Briefcase, 
  UserCircle,
  Plus,
  Search,
  Sparkles,
  ArrowRight
} from "lucide-react";

interface EmptyStateProps {
  type: "contacts" | "companies" | "deals" | "buyers" | "coi" | "search";
  title?: string;
  description?: string;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  showCreateButton?: boolean;
  className?: string;
}

const emptyStateConfig = {
  contacts: {
    icon: Users,
    defaultTitle: "No contacts yet",
    defaultDescription: "Add your first contact to start building your network. Import from Outlook, scan business cards, or add manually.",
    defaultAction: "Add Contact",
    iconColor: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-100 dark:bg-purple-900/30",
  },
  companies: {
    icon: Building2,
    defaultTitle: "No companies yet",
    defaultDescription: "Add companies you're tracking for M&A opportunities. Use the Chrome extension to capture from websites.",
    defaultAction: "Add Company",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
  },
  deals: {
    icon: Briefcase,
    defaultTitle: "No active deals",
    defaultDescription: "Start tracking your M&A deals. Associate companies, buyers, and track progress through your pipeline.",
    defaultAction: "Create Deal",
    iconColor: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
  },
  buyers: {
    icon: UserCircle,
    defaultTitle: "No buyers in network",
    defaultDescription: "Build your buyer network to match with sell-side opportunities. Add strategic and financial buyers.",
    defaultAction: "Add Buyer",
    iconColor: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-100 dark:bg-blue-900/30",
  },
  coi: {
    icon: Users,
    defaultTitle: "No COIs yet",
    defaultDescription: "Add Centers of Influence - attorneys, CPAs, bankers who can provide warm introductions to opportunities.",
    defaultAction: "Add COI",
    iconColor: "text-cyan-600 dark:text-cyan-400",
    bgColor: "bg-cyan-100 dark:bg-cyan-900/30",
  },
  search: {
    icon: Search,
    defaultTitle: "No results found",
    defaultDescription: "Try adjusting your search terms or filters. You can search by name, company, email, or phone.",
    defaultAction: "Clear Filters",
    iconColor: "text-muted-foreground",
    bgColor: "bg-muted",
  },
};

export function EmptyState({
  type,
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  showCreateButton = true,
  className,
}: EmptyStateProps) {
  const config = emptyStateConfig[type];
  const Icon = config.icon;

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
  };

  return (
    <Card className={cn("border border-dashed border-border/50", className)}>
      <CardContent className="p-8 text-center">
        <div className={cn("w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4", config.bgColor)}>
          <Icon className={cn("w-8 h-8", config.iconColor)} />
        </div>
        
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {title || config.defaultTitle}
        </h3>
        
        <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
          {description || config.defaultDescription}
        </p>

        {showCreateButton && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {actionHref ? (
              <Link href={actionHref}>
                <Button className="gap-2 gold-gradient">
                  <Plus className="w-4 h-4" />
                  {actionLabel || config.defaultAction}
                </Button>
              </Link>
            ) : onAction ? (
              <Button onClick={handleAction} className="gap-2 gold-gradient">
                <Plus className="w-4 h-4" />
                {actionLabel || config.defaultAction}
              </Button>
            ) : null}

            {type === "contacts" && (
              <Button variant="outline" className="gap-2">
                <Sparkles className="w-4 h-4" />
                Scan Business Card
              </Button>
            )}
          </div>
        )}

        {/* Quick tips */}
        <div className="mt-8 pt-6 border-t border-border/50">
          <p className="text-xs text-muted-foreground mb-3">Quick tips:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {getQuickTips(type).map((tip, index) => (
              <span 
                key={index}
                className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground"
              >
                {tip}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getQuickTips(type: string): string[] {
  switch (type) {
    case "contacts":
      return ["Cmd+K to search", "Shift+C quick add", "Import from Outlook"];
    case "companies":
      return ["Use Chrome extension", "Import CSV", "Auto-enrichment"];
    case "deals":
      return ["Track pipeline stages", "Associate buyers", "Log activities"];
    case "buyers":
      return ["Add investment criteria", "Track relationships", "Match with deals"];
    case "coi":
      return ["Track referrals", "Log interactions", "Warm intros"];
    default:
      return [];
  }
}

// Specialized empty states for common scenarios
export function EmptySearchResults({ 
  query, 
  onClear 
}: { 
  query: string; 
  onClear: () => void;
}) {
  return (
    <Card className="border border-dashed border-border/50">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-muted flex items-center justify-center mb-4">
          <Search className="w-8 h-8 text-muted-foreground" />
        </div>
        
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No results for "{query}"
        </h3>
        
        <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
          Try adjusting your search terms or check your spelling. You can also search by email, phone, or company name.
        </p>

        <Button variant="outline" onClick={onClear} className="gap-2">
          Clear Search
          <ArrowRight className="w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

export function LoadingEmptyState({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="border border-dashed border-border/30">
          <CardContent className="p-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-muted animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-5 w-48 bg-muted rounded animate-pulse" />
                <div className="h-4 w-64 bg-muted rounded animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
